import {
  ICellRendererParams,
  RowClassParams,
  ValueFormatterParams,
} from "ag-grid-community";

import { OverflowDisplayTooltip } from "../../OverflowDisplayTooltip";
import { cn } from "../../utils";
import { ContextMenu } from "../ContextMenu";
import { ContextMenuItems } from "../ContextMenu/types";

import { BaseTableData, SimpleTableColumn, type SimpleTableProps } from "./types";

export function createSimpleCellRenderer<T extends BaseTableData>(
  column: SimpleTableColumn<T>,
  contextMenuItems?: ContextMenuItems<T>[],
  getRowHref?: (row: T) => string | undefined,
  onRowLinkClick?: SimpleTableProps<T>["onRowLinkClick"],
) {
  return (params: ICellRendererParams<T>) => {
    let cellContent: React.ReactNode;

    const { value, data } = params;

    if (typeof column.cellRenderer === "function") {
      cellContent = column.cellRenderer(params);
    } else if (column.cellRenderer && typeof column.cellRenderer !== "string") {
      const CellRenderer = column.cellRenderer as React.ComponentType<
        ICellRendererParams<T>
      >;
      cellContent = <CellRenderer {...params} />;
    } else if (
      column.valueFormatter &&
      typeof column.valueFormatter === "function"
    ) {
      cellContent = column.valueFormatter(params as ValueFormatterParams<T>);
    } else {
      cellContent = value;
    }

    const rowHref = data && !column.suppressRowLink ? getRowHref?.(data) : undefined;
    const linkContent = rowHref && data ? (
      <a
        href={rowHref}
        className={cn(
          "flex h-full w-full items-center px-3 leading-none text-inherit no-underline",
          column.cellContentClassName,
        )}
        style={column.cellContentStyle}
        onClick={event => onRowLinkClick?.(data, event)}
      >
        {cellContent}
      </a>
    ) : null;

    if (contextMenuItems && contextMenuItems.length > 0 && data) {
      return (
        <ContextMenu<T>
          data={data}
          modal={false}
          className={cn(
            "h-full w-full",
            !rowHref && "flex items-center px-3 leading-none",
            !rowHref && column.cellContentClassName,
          )}
          style={rowHref ? undefined : column.cellContentStyle}
          isDisabled={!contextMenuItems || contextMenuItems.length === 0}
          contextMenuItems={contextMenuItems}
        >
          {linkContent ?? cellContent}
        </ContextMenu>
      );
    }

    if (linkContent) {
      return linkContent;
    }

    if (typeof cellContent === "string") {
      return (
        <OverflowDisplayTooltip content={cellContent} className="px-3">
          {cellContent}
        </OverflowDisplayTooltip>
      );
    }

    return (
      <div
        className={cn(
          "flex h-full w-full items-center px-3 leading-none",
          column.cellContentClassName,
        )}
        style={column.cellContentStyle}
      >
        {cellContent}
      </div>
    );
  };
}

export function getSimpleRowClasses<T extends BaseTableData>({
  hoverable,
  onRowClick,
  onRowDoubleClick,
  striped,
  params,
  isHighlighted,
}: {
  hoverable: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowClick?: (row: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowDoubleClick?: (row: any) => void;
  striped: boolean;
  params: RowClassParams<T>;
  isHighlighted?: (row: T) => boolean;
}): string {
  const classes: string[] = [];

  const shouldHighlight =
    isHighlighted && params.data && isHighlighted(params.data);

  if (shouldHighlight) {
    classes.push("bg-blue-50 hover:!bg-blue-100");
  } else {
    if (hoverable) {
      classes.push("hover:bg-gray-50");
    }

    if (striped && params.node?.rowIndex && params.node.rowIndex % 2 === 1) {
      classes.push("bg-gray-50/50");
    }
  }

  if (onRowClick || onRowDoubleClick) {
    classes.push("cursor-pointer");
  }

  return classes.join(" ");
}

export function createSimpleContainerStyle({
  hoverable,
  headerHeight,
  rowHeight,
  compact,
}: {
  hoverable: boolean;
  headerHeight: number;
  rowHeight: number;
  compact: boolean;
}): React.CSSProperties {
  return {
    "--ag-header-background-color": "rgb(249 250 251)",
    "--ag-header-foreground-color": "rgb(75 85 99)",
    "--ag-row-hover-color": hoverable ? "rgb(249 250 251)" : "transparent",
    "--ag-border-color": "rgb(229 231 235)",
    "--ag-header-height": `${headerHeight}px`,
    "--ag-row-height": `${rowHeight}px`,
    "--ag-cell-horizontal-padding": "12px",
    "--ag-font-size": compact ? "13px" : "14px",
    "--ag-header-font-size": compact ? "12px" : "13px",
    "--ag-line-height": compact ? "1.2" : "1.5",
  } as React.CSSProperties;
}
